import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../api';

/**
 * WizardTour — a focused mini-tour that fires the first time a user opens
 * the Create Clan wizard. Complements (does not overlap) the general
 * OnboardingTour which lives at the /home level. Uses the same DIY
 * spotlight primitives so styling stays consistent across all tours.
 *
 * Steps stay on wizard Step 1 (no navigation) so we don't fight the
 * wizard's own state machine. On the final step we tell the creator what
 * to expect on Steps 2 and 3.
 *
 * Copy lives in i18n under the `tours.wizard.*` namespace — the STEPS
 * array here holds only structural metadata (id, target, placement) so
 * translations can override per-locale without touching JS.
 *
 * Props:
 *  - wizardStep: current step number of the wizard (1..3). Tour only
 *    auto-boots when the user is on Step 1.
 *  - forceStart: bump this number (via the header replay pill) to force
 *    the tour to restart on demand.
 */
const STEPS = [
  { id: 'welcome',         target: null                                        },
  { id: 'openingPrice',    target: '[data-testid="cc-ticket-grid"]', placement: 'bottom' },
  { id: 'communityStyle',  target: '[data-testid="cc-style-grid"]',  placement: 'bottom' },
  { id: 'earnings',        target: '[data-testid="cc-preview"]',     placement: 'top'    },
  { id: 'sqrtCurve',       target: null                                        },
  { id: 'socials',         target: null                                        },
  { id: 'done',            target: null                                        },
];

const PADDING = 8;

export default function WizardTour({ wizardStep = 1, forceStart = 0 }) {
  const { user, refreshMe } = useAuth();
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const bootedRef = useRef(false);

  // Auto-boot on first visit
  useEffect(() => {
    if (bootedRef.current) return;
    if (!user || user === false) return;
    if (user.wizard_tour_completed_at) return;
    if (wizardStep !== 1) return;
    bootedRef.current = true;
    const t = setTimeout(() => {
      setActive(true);
      setIdx(0);
    }, 700);
    return () => clearTimeout(t);
  }, [user, wizardStep]);

  // Manual replay
  useEffect(() => {
    if (forceStart > 0) {
      setActive(true);
      setIdx(0);
    }
  }, [forceStart]);

  const step = active ? STEPS[idx] : null;

  useLayoutEffect(() => {
    if (!active || !step) { setRect(null); setReady(false); return; }
    if (!step.target) { setRect(null); setReady(true); return; }

    let cancelled = false;
    let tries = 0;
    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // small delay so the scroll settles before measuring
        setTimeout(() => {
          if (cancelled) return;
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          setReady(true);
        }, 250);
        return;
      }
      if (++tries < 15) setTimeout(measure, 100);
      else { setReady(true); setRect(null); }
    };
    measure();

    const onScroll = () => {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [active, idx, step]);

  const finish = useCallback(async () => {
    setActive(false);
    try {
      await api.post('/auth/wizard-tour-complete');
      await refreshMe();
    } catch (_) { /* non-fatal */ }
  }, [refreshMe]);

  const next = useCallback(() => {
    if (idx >= STEPS.length - 1) { finish(); return; }
    setIdx((i) => i + 1);
  }, [idx, finish]);
  const back = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  if (!active || !step || !ready) return null;

  const isCentered = !step.target || !rect;
  const tooltipStyle = (() => {
    if (isCentered) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    const vw = window.innerWidth;
    const tooltipW = Math.min(340, vw - 24);
    let top = rect.top + rect.height + 12;
    let left = rect.left + rect.width / 2 - tooltipW / 2;
    left = Math.max(12, Math.min(left, vw - tooltipW - 12));
    top = Math.max(12, top);
    return { top: `${top}px`, left: `${left}px`, width: `${tooltipW}px` };
  })();

  return (
    <div className="tour-root" data-testid="wizard-tour-root" role="dialog" aria-modal="true">
      {isCentered ? (
        <div className="tour-backdrop tour-backdrop-solid" onClick={next} />
      ) : (
        <>
          <div className="tour-backdrop-panel" style={{ top: 0, left: 0, right: 0, height: rect.top - PADDING }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top - PADDING, left: 0, width: rect.left - PADDING, height: rect.height + PADDING * 2 }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top - PADDING, left: rect.left + rect.width + PADDING, right: 0, height: rect.height + PADDING * 2 }} />
          <div className="tour-backdrop-panel" style={{ top: rect.top + rect.height + PADDING, left: 0, right: 0, bottom: 0 }} />
          <div
            className="tour-spotlight"
            style={{
              top: rect.top - PADDING,
              left: rect.left - PADDING,
              width: rect.width + PADDING * 2,
              height: rect.height + PADDING * 2,
            }}
          />
        </>
      )}

      <div className="tour-tooltip" style={tooltipStyle} data-testid={`wizard-tour-step-${step.id}`}>
        <div className="tour-step-count">{t('tours.wizard.stepCount', { current: idx + 1, total: STEPS.length })}</div>
        <div className="tour-title">{t(`tours.wizard.${step.id}Title`)}</div>
        <div className="tour-body">{t(`tours.wizard.${step.id}Body`)}</div>
        <div className="tour-actions">
          <button className="tour-btn-ghost" onClick={finish} data-testid="wizard-tour-skip-btn">
            {t('tours.common.skip')}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {idx > 0 && (
              <button className="tour-btn-ghost" onClick={back} data-testid="wizard-tour-back-btn">
                {t('tours.common.back')}
              </button>
            )}
            <button className="tour-btn-primary" onClick={next} data-testid="wizard-tour-next-btn">
              {idx >= STEPS.length - 1 ? t('tours.common.gotIt') : t('tours.common.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
